<template>
	<div
		class="hotel-list-filter"
		:class="{ 'hotel-list-filter--mobile': !isDesktop }"
	>
		<p
			v-if="isDesktop"
			class="hotel-list-filter__title"
		>
			Sortiert nach
		</p>
		<DropdownField
			ref="dropdown"
			v-model="fieldValue"
			class="dropdown--hotel-list-filter"
			:class="{ disabled: disabled, 'dropdown--hotel-list-filter--mobile': !isDesktop }"
			placeholder="Beliebtheit"
			:show-footer="false"
			:disabled="disabled"
			:show-toggle-icon="isDesktop"
			:label="!isDesktop ? 'Sortiert nach' : ''"
			@DropdownField:OutsideClick="handleOutsideClick"
			@keydown.enter.stop="handleOpen($event)"
			@keydown.space.stop="handleOpen($event)"
		>
			<template
				v-if="!isDesktop"
				#iconLeading
			>
				<FaIcon
					class="dropdown-field__icon"
					:icon="['fa-solid', 'fa-arrow-down-wide-short']"
				/>
			</template>
			<template #default>
				<ul
					class="dropdown-field__list"
					role="menu"
				>
					<li
						v-for="(item, index) in orderTypes"
						:key="item.name"
						class="dropdown-field__list-item"
						role="menuitem"
					>
						<button
							class="dropdown-field__button"
							type="button"
							:class="{ 'is-selected': proxy === index }"
							@click="onClick(index, $event)"
							@keydown.enter.stop="onClick(index, $event)"
							@keydown.space="onClick(index, $event)"
							@keydown.up.prevent="checkNavigate"
							@keydown.down.prevent="checkNavigate"
						>
							{{ item.name }}
						</button>
					</li>
				</ul>
			</template>
		</DropdownField>
	</div>
</template>

<script lang="ts" setup>
import DropdownField from '@lmt-rpb/DropdownField/DropdownField.vue';
import { computed, ref } from 'vue';
import { useStore } from '@/components/common/store';
import { updateURL } from '@components/common/services/url';
import { formToParams } from '@components/common/services/transform';
import type { SearchFormDataType } from '@interfaces/search-form';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';

type Props = {
	disabled?: boolean;
};

withDefaults(defineProps<Props>(), { disabled: false });

const store = useStore();
const { isDesktop } = storeToRefs(useBreakpointStore());
const orderTypes: { name: string; mobileName: string; value: 'Price' | 'Popular' | 'Discount' | 'Reviews' }[] = [
	{ name: 'Beliebtheit', mobileName: 'Beliebtheit', value: 'Popular' },
	{ name: 'Niedrigster Preis', mobileName: 'Preis', value: 'Price' },
	{ name: 'Größte Ersparnis', mobileName: 'Ersparnis', value: 'Discount' },
	{ name: 'Beste Bewertung', mobileName: 'Bewertung', value: 'Reviews' },
];

const orderType = computed({
	get() {
		return store.state.searchMask.sortingHotel;
	},
	set(value: number) {
		store.commit('searchMask/updateFormData', { sortingHotel: orderTypes[value].value });
	},
});
const dropdown = ref<InstanceType<typeof DropdownField> | null>(null);
const formData = computed((): SearchFormDataType => store.state.searchMask);
const proxy = computed(() => {
	const index = orderTypes.findIndex((item) => item.value === orderType.value);
	return index !== -1 ? index : null;
});

const fieldValue = computed((): string => {
	if (proxy.value === null) {
		return '';
	}

	const selectedItem = orderTypes[proxy.value];
	if (!selectedItem) {
		return '';
	}

	return isDesktop.value ? selectedItem.name : selectedItem.mobileName;
});

const handleAnalyticsEvent = (index: number) => {
	SelectContentAnalytics.selectContent({
		cId: 'list_sort',
		cType: 'ux_hotellist_click',
		index: index + 1,
	});
};

const onClick = (index: number, e: MouseEvent | KeyboardEvent) => {
	updateURL(formToParams(formData.value, 'hotelList'));
	orderType.value = index;
	handleAnalyticsEvent(index);
	e.preventDefault();
	dropdown.value?.closeDropdown();
};

const navIndex = ref(0);

const checkNavigate = (e: KeyboardEvent) => {
	const list = document.querySelectorAll('.dropdown-field__button');
	const maxPos = list.length - 1;

	if (e.key === 'ArrowUp') {
		navIndex.value = navIndex.value === 0 ? maxPos : navIndex.value - 1;
	} else if (e.key === 'ArrowDown') {
		navIndex.value = navIndex.value === maxPos ? 0 : navIndex.value + 1;
	}

	(list[navIndex.value] as HTMLButtonElement)?.focus();
};

const handleOutsideClick = () => {
	dropdown.value?.closeDropdown();
};

const handleOpen = (e: KeyboardEvent) => {
	if (e.key === 'Enter' || e.key === ' ') {
		e.preventDefault();
		dropdown.value?.toggleDropdown();
	}
};
</script>

<style lang="scss" scoped>
.hotel-list-filter {
	display: flex;
	align-items: center;

	&__title {
		font-size: $font-small-2;
		line-height: $line-height-text;
		margin: 0 0.8rem 0 0;
	}

	:deep(.dropdown .dropdown__box) {
		overflow: visible;

		@media (min-width: $breakpoint-verylarge) {
			width: calc(100% + 0.6rem);
		}
	}

	&--mobile {
		.dropdown-field {
			&__list-item {
				.dropdown-field__button {
					padding: 0.8rem 3.2rem;
					white-space: nowrap;
				}
			}
		}

		:deep() {
			.dropdown {
				.dropdown__box {
					overflow: hidden;
					transition: none;
				}

				&.is-open {
					.dropdown__box {
						overflow: visible;
						transition: none;
					}
				}
			}
		}
	}
}

.dropdown-field {
	&__icon {
		color: $color-text-upselling;
	}

	&__list {
		margin: 0;
		padding: 0.8rem 0;
		overflow: visible;
		list-style: none;
	}

	/* stylelint-disable-next-line no-descending-specificity */
	&__button {
		margin: 0;
		border: none;
		background: none;
		font-weight: $font-weight-semibold;
		text-align: left;
		cursor: pointer;
		color: $color-black;
	}

	&__list-item {
		display: block;
		font-weight: $font-weight-semibold;
		text-align: left;

		/* stylelint-disable-next-line no-descending-specificity */
		.dropdown-field__button {
			font-size: $font-small-1;
			padding: 0.8rem 1.6rem;
			width: 100%;
			position: relative;

			&.is-selected {
				background: $color-link;
				color: $color-white;
			}

			&:focus,
			&:active,
			&:focus-visible {
				outline: 0.2rem solid transparent;

				&:not(.is-selected) {
					background: $color-primary-l6;
					color: $color-black;
				}

				&::before {
					content: '';
					position: absolute;
					top: -0.4rem;
					left: -0.35rem;
					right: -0.39rem;
					/* stylelint-disable-next-line declaration-block-no-redundant-longhand-properties */
					bottom: -0.39rem;
					border: 0.2rem solid $color-link;
					z-index: 2;
				}
			}
		}

		&:hover {
			background: $color-primary-l6;
		}
	}
}
</style>
